// src/pages/Product/ProductDetail.js
import { useState } from 'react';
import { useParams, Link, useLocation } from 'react-router-dom';
import { useProduct } from '../../hooks/useProducts';
import { useCart } from '../../context/CartContext';
import { useAuth } from '../../context/AuthContext';
import { orderService } from '../../services/api';
import { useDocumentTitle } from '../../hooks/useDocumentTitle';
import './ProductDetail.css';

export default function ProductDetail() {
  const { id }      = useParams();
  const location    = useLocation();
  const { product, loading, error } = useProduct(id);

  // Le titre de l'onglet reprend le nom du produit une fois celui-ci chargé.
  useDocumentTitle(product ? product.nom : 'Produit');
  const { addItem } = useCart();
  const { isLoggedIn } = useAuth();
  const [qty,      setQty]     = useState(1);
  const [added,    setAdded]   = useState(false);
  const [imgIndex, setImgIndex]= useState(0);

  // Formulaire d'avis
  const [avis, setAvis]           = useState({ note: 5, commentaire: '' });
  const [avisMsg, setAvisMsg]     = useState(null); // { type: 'ok'|'err', text }
  const [avisLoading, setAvisLoading] = useState(false);

  async function handleAvisSubmit(e) {
    e.preventDefault();
    setAvisMsg(null);
    setAvisLoading(true);
    try {
      const res = await orderService.addReview({
        product_id: Number(id),
        note: avis.note,
        commentaire: avis.commentaire.trim(),
      });
      setAvisMsg({ type: 'ok', text: res.message || 'Avis soumis — en attente de validation.' });
      setAvis({ note: 5, commentaire: '' });
    } catch (err) {
      setAvisMsg({ type: 'err', text: err.message });
    } finally {
      setAvisLoading(false);
    }
  }

  if (loading) return <div style={{ paddingTop: 'var(--navbar-h)' }}><div className="spinner" /></div>;
  if (error || !product) return (
    <div style={{ paddingTop: 'var(--navbar-h)', textAlign: 'center', padding: '120px 24px', color: 'var(--gris)' }}>
      <p style={{ fontSize: '1.2rem', marginBottom: 24 }}>Produit introuvable.</p>
      <Link to="/catalogue" className="btn-or">Retour au catalogue</Link>
    </div>
  );

  const rupture     = product.stock === 0;
  const stockLimite = product.stock > 0 && product.stock <= 5;
  const images      = product.images?.length > 0
    ? product.images
    : [`https://placehold.co/600x750/1a1a1a/c9a84c?text=${encodeURIComponent(product.nom)}`];

  function handleAdd() {
    if (rupture) return;
    addItem(product, qty);
    setAdded(true);
    setTimeout(() => setAdded(false), 2500);
  }

  const note_moy = product.reviews?.length
    ? (product.reviews.reduce((s, r) => s + r.note, 0) / product.reviews.length).toFixed(1)
    : null;

  return (
    <div className="product-detail">
      <div className="container product-detail__inner">
        <nav className="product-detail__breadcrumb" aria-label="Fil d'ariane">
          <Link to="/">Accueil</Link><span>›</span>
          <Link to="/catalogue">Catalogue</Link><span>›</span>
          {product.categorie && <><Link to={`/catalogue/${product.categorie.toLowerCase()}`}>{product.categorie}</Link><span>›</span></>}
          <span>{product.nom}</span>
        </nav>

        <div className="product-detail__grid">
          {/* Galerie */}
          <div className="product-detail__gallery">
            <div className="product-detail__main-img">
              <img src={images[imgIndex]} alt={product.nom} />
              {product.nouveaute && <span className="badge" style={{ position:'absolute', top:12, left:12 }}>Nouveau</span>}
              {rupture && <span className="badge" style={{ position:'absolute', top:12, left:12, background:'#555', color:'#aaa' }}>Épuisé</span>}
            </div>
            {images.length > 1 && (
              <div className="product-detail__thumbs">
                {images.map((img, i) => (
                  <button key={i} onClick={() => setImgIndex(i)}
                    className={`product-detail__thumb${i === imgIndex ? ' active' : ''}`}>
                    <img src={img} alt="" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Infos */}
          <div className="product-detail__info">
            <span className="product-detail__categorie">{product.categorie}</span>
            <h1 className="product-detail__nom">{product.nom}</h1>

            {note_moy && (
              <div className="product-detail__rating">
                <span>{'★'.repeat(Math.round(note_moy))}{'☆'.repeat(5 - Math.round(note_moy))}</span>
                <span>{note_moy} ({product.reviews.length} avis)</span>
              </div>
            )}

            <div className="product-detail__prix-wrap">
              <span className="product-detail__prix">{Number(product.prix).toFixed(2)} €</span>
              {product.prix_barre && <span className="product-detail__prix-barre">{Number(product.prix_barre).toFixed(2)} €</span>}
            </div>

            <p className={`product-detail__stock${rupture ? ' rupture' : stockLimite ? ' limite' : ' ok'}`}>
              {rupture ? '⚠ Produit épuisé' : stockLimite ? `⚡ Plus que ${product.stock} en stock` : '✓ En stock'}
            </p>

            <p className="product-detail__description">{product.description}</p>

            {!rupture && (
              <div className="product-detail__actions">
                <div className="product-detail__qty">
                  <button onClick={() => setQty(q => Math.max(1, q - 1))}>−</button>
                  <span>{qty}</span>
                  <button onClick={() => setQty(q => Math.min(product.stock, q + 1))}>+</button>
                </div>
                <button className={`btn-or product-detail__add${added ? ' added' : ''}`} onClick={handleAdd}>
                  {added ? '✓ Ajouté au panier !' : 'Ajouter au panier'}
                </button>
              </div>
            )}

            <ul className="product-detail__garanties">
              <li>🚚 Livraison 2-5 jours ouvrés</li>
              <li>🔒 Paiement 100% sécurisé (Stripe)</li>
              <li>↩️ Retours sous 14 jours</li>
            </ul>
          </div>
        </div>

        {/* Laisser un avis */}
        <section className="product-detail__reviews" style={{ marginTop: 48 }}>
          <h2>Donner mon avis</h2>
          {isLoggedIn ? (
            <form onSubmit={handleAvisSubmit}
              style={{ maxWidth: 560, display: 'flex', flexDirection: 'column', gap: 14, marginTop: 16 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }} role="radiogroup" aria-label="Note sur 5">
                {[1, 2, 3, 4, 5].map(n => (
                  <button key={n} type="button" role="radio" aria-checked={avis.note === n}
                    onClick={() => setAvis(a => ({ ...a, note: n }))}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '1.6rem',
                             color: n <= avis.note ? 'var(--or)' : '#444', padding: 2 }}>
                    ★
                  </button>
                ))}
                <span style={{ marginLeft: 8, color: 'var(--gris)', fontSize: '0.85rem' }}>{avis.note}/5</span>
              </div>
              <textarea rows="3" placeholder="Votre commentaire (facultatif)"
                value={avis.commentaire}
                onChange={e => setAvis(a => ({ ...a, commentaire: e.target.value }))}
                style={{ padding: '12px 14px', background: '#1a1a1a', border: '1px solid rgba(255,255,255,0.12)',
                         borderRadius: 4, color: 'var(--blanc)', fontFamily: 'var(--font-corps)', resize: 'vertical' }} />
              {avisMsg && (
                <p style={{ fontSize: '0.85rem', color: avisMsg.type === 'ok' ? 'var(--succes)' : 'var(--erreur)' }}>
                  {avisMsg.text}
                </p>
              )}
              <button type="submit" className="btn-or" disabled={avisLoading}
                style={{ alignSelf: 'flex-start' }}>
                {avisLoading ? 'Envoi…' : 'Publier mon avis'}
              </button>
            </form>
          ) : (
            <p style={{ color: 'var(--gris)', marginTop: 12 }}>
              <Link to="/connexion" state={{ from: location }} style={{ color: 'var(--or)' }}>Connectez-vous</Link>
              {' '}pour laisser un avis.
            </p>
          )}
        </section>

        {product.reviews?.length > 0 && (
          <section className="product-detail__reviews">
            <h2>Avis clients ({product.reviews.length})</h2>
            <div className="product-detail__reviews-grid">
              {product.reviews.map(r => (
                <article key={r.id} className="product-detail__review-card">
                  <div className="product-detail__review-header">
                    <span style={{ color: 'var(--or)' }}>{'★'.repeat(r.note)}{'☆'.repeat(5 - r.note)}</span>
                    <span style={{ color: 'var(--gris)', fontSize: '0.82rem' }}>{r.auteur}</span>
                  </div>
                  <p style={{ fontSize: '0.9rem', color: 'var(--gris-light)', margin: '8px 0 4px' }}>{r.commentaire}</p>
                  <p style={{ fontSize: '0.75rem', color: 'var(--gris)' }}>{new Date(r.date).toLocaleDateString('fr-FR')}</p>
                </article>
              ))}
            </div>
          </section>
        )}
      </div>
    </div>
  );
}